import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GlobalSearch from './GlobalSearch';
import Notifications from './Notifications';
import { getCurrentClockStatus, clockIn, clockOut } from '../services/clockInOut';
import { getUnreadMessageCount } from '../services/messages';
import { isAdminOrManager, isTech } from '../utils/roleHelpers';
import './Navigation.css';

export default function Navigation({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const isAdminOrMgr = isAdminOrManager(user);
  const isUser = isTech(user);
  
  const [clockStatus, setClockStatus] = useState({ isClockedIn: false, clockInRecord: null });
  const [clockLoading, setClockLoading] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const loadClockStatus = useCallback(async () => {
    if (!isUser) return;
    try {
      const status = await getCurrentClockStatus();
      setClockStatus(status);
    } catch (error) {
      console.error('Error loading clock status:', error);
    }
  }, [isUser]);

  // Load clock status on mount and when user changes
  useEffect(() => {
    if (isUser) {
      loadClockStatus();
      // Refresh clock status every 30 seconds
      const interval = setInterval(loadClockStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [isUser, user?.id, loadClockStatus]);

  // Load unread message count
  const loadUnreadMessageCount = useCallback(async () => {
    try {
      const count = await getUnreadMessageCount();
      setUnreadMessageCount(count);
    } catch (error) {
      console.error('Error loading unread message count:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadUnreadMessageCount();
      // Refresh message count every 15 seconds
      const interval = setInterval(loadUnreadMessageCount, 15000);
      return () => clearInterval(interval);
    }
  }, [user, loadUnreadMessageCount]);

  const handleClockIn = async () => {
    try {
      setClockLoading(true);
      await clockIn();
      await loadClockStatus();
      alert('Clocked in successfully!');
    } catch (error) {
      alert('Failed to clock in: ' + error.message);
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setClockLoading(true);
      await clockOut();
      await loadClockStatus();
      alert('Clocked out successfully!');
    } catch (error) {
      alert('Failed to clock out: ' + error.message);
    } finally {
      setClockLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="app-with-nav">
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <Link to="/dashboard" className="logo-link">
              <h1 className="logo">Menlo Atherton Glass</h1>
              <p className="tagline">The Clear Choice</p>
            </Link>
          </div>
          <nav className="main-nav">
            {/* TEMPORARY: Show all links for testing - normally these require admin/manager role */}
            <Link
              to="/dashboard"
              className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
            >
              Dashboard
            </Link>
            <Link
              to="/clients"
              className={`nav-link ${isActive('/clients') ? 'active' : ''}`}
            >
              Clients
            </Link>
            <Link
              to="/open-estimates"
              className={`nav-link ${isActive('/open-estimates') ? 'active' : ''}`}
            >
              Open Estimates
            </Link>
            <Link
              to="/unpaid-invoices"
              className={`nav-link ${isActive('/unpaid-invoices') ? 'active' : ''}`}
            >
              Unpaid Invoices
            </Link>
            <Link
              to="/calendar"
              className={`nav-link ${isActive('/calendar') ? 'active' : ''}`}
            >
              Calendar
            </Link>
            <Link
              to="/task-history"
              className={`nav-link ${isActive('/task-history') ? 'active' : ''}`}
            >
              Task History
            </Link>
            <Link
              to="/messages"
              className={`nav-link ${isActive('/messages') ? 'active' : ''}`}
            >
              Messages
              {unreadMessageCount > 0 && (
                <span className="nav-unread-badge">{unreadMessageCount}</span>
              )}
            </Link>
          </nav>
          <div className="header-actions">
            <div className="header-actions-row">
              {isAdminOrMgr && <Notifications />}
            </div>
            {isUser && (
              <div className="clock-in-out-buttons">
                {clockStatus.isClockedIn ? (
                  <button
                    className="btn-clock-out"
                    onClick={handleClockOut}
                    disabled={clockLoading}
                    title={`Clocked in since ${clockStatus.clockInRecord ? new Date(clockStatus.clockInRecord.clock_in_time).toLocaleTimeString() : ''}`}
                  >
                    ⏰ Clock Out
                  </button>
                ) : (
                  <button
                    className="btn-clock-in"
                    onClick={handleClockIn}
                    disabled={clockLoading}
                  >
                    ⏰ Clock In
                  </button>
                )}
              </div>
            )}
            <span className="user-email">{user?.email}</span>
            <button className="btn-logout" onClick={handleLogout}>
              Logout
            </button>
            {isAdminOrMgr && <GlobalSearch />}
          </div>
        </div>
      </header>
      <main className="app-main">
        {children}
      </main>
      <footer className="app-footer">
        <p>Software by Apex TSG</p>
      </footer>
      <Link
        to="/settings"
        className="settings-fab"
        title="Settings"
        aria-label="Settings"
      >
        ⚙
      </Link>
      <Link
        to="/support"
        className="help-fab"
        title="Support & Bug Reports"
        aria-label="Support"
      >
        ?
      </Link>
    </div>
  );
}
